import { yupResolver } from "@hookform/resolvers/yup";
import { Box, type DrawerProps, Grid, IconButton, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { DialogModal } from "Components/_Shared/DialogModal";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type MovableAssetItemFormType, getMovableAssetItemFormSchema } from "Forms/Schemas/Assets/movableAssetItemFormSchema";
import {
  getGetAssetsOverviewQueryKey,
  getListMovableAssetItemsQueryKey,
  useCreateMovableAssetItem,
  useDeleteAssetItem,
  useUpdateMovableAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type MovableAssetItem, MovableAssetItemMovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { MOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly movableAsset?: MovableAssetItem;
} & DrawerProps;

export const MovableAssetItemDrawer: FC<Props> = ({ onClose, movableAsset, open, ...rest }) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const { t } = useResource();
  const queryClient = useQueryClient();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListMovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const { mutate: create, isPending: isCreating } = useCreateMovableAssetItem();
  const { mutate: update, isPending: isUpdating } = useUpdateMovableAssetItem();
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteAssetItem();

  const isMutating = isCreating || isUpdating || isDeleting;

  const movableAssets: Option[] = (Object.keys(MovableAssetItemMovableSubType) as Array<MovableAssetItemMovableSubType>).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType[MOVABLE_SUBTYPE_RESOURCE_KEY[value]]),
  }));

  const methods = useForm<MovableAssetItemFormType>({
    resolver: yupResolver(getMovableAssetItemFormSchema(t)),
    mode: "onBlur",
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (open) {
      const defaultValues = {
        movableAssetType: movableAsset?.movableSubType,
        name: movableAsset?.name,
        value: movableAsset?.amount.value,
        note: movableAsset?.note ?? undefined,
        vehicleIdentificationNumber: movableAsset?.vehicleIdentificationNumber ?? undefined,
        vehicleLicensePlate: movableAsset?.vehicleLicensePlate ?? undefined,
        vehiclePrice: movableAsset?.vehiclePrice?.value,
        companyIdentificationNumber: movableAsset?.companyIdentificationNumber ?? undefined,
        businessSector: movableAsset?.businessSector ?? undefined,
      };
      reset(defaultValues);
      return;
    }

    reset({});
  }, [movableAsset, open, reset]);

  const movableAssetType = watch("movableAssetType");
  const isVehicle = movableAssetType === MovableAssetItemMovableSubType.vehicle;
  const isCompany = movableAssetType === MovableAssetItemMovableSubType.company;

  const onSubmit = (data: MovableAssetItemFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        movableSubType: data.movableAssetType as MovableAssetItemMovableSubType,
        isCoveredByInsurance: !!movableAsset?.isCoveredByInsurance,
        isUnderFinancing: !!movableAsset?.isUnderFinancing,
        isUsableForGoals: !!movableAsset?.isUsableForGoals,
        vehicleIdentificationNumber: isVehicle ? data.vehicleIdentificationNumber || undefined : undefined,
        vehicleLicensePlate: isVehicle ? data.vehicleLicensePlate || undefined : undefined,
        vehiclePrice:
          isVehicle && data.vehiclePrice
            ? { value: data.vehiclePrice, currencyCode: "CZK", frequency: AmountFrequency.one_time }
            : undefined,
        companyIdentificationNumber: isCompany ? data.companyIdentificationNumber || undefined : undefined,
        businessSector: isCompany ? data.businessSector || undefined : undefined,
      };

      if (!movableAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: movableAsset.guid, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Edited));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  const onDelete = () => {
    try {
      deleteItem(
        { analysisId, versionId, itemId: movableAsset?.guid || "" },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Movable.Deleted));
            invalidate();
            setDeleteModalOpen(false);
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  return (
    <>
      <Drawer anchor="right" open={open} {...rest} onClose={onClose}>
        <FormProvider {...methods}>
          <Box component="form" display="flex" flexDirection="column" height="100%" onSubmit={handleSubmit(onSubmit)}>
            <Flex flexShrink={0} justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
              <Typography variant="h2">
                {movableAsset ? t(Resources.AnalysisDetail.Assets.Movable.Edit) : t(Resources.AnalysisDetail.Assets.Movable.Add)}
              </Typography>

              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Flex>
            <DrawerContent sx={{ flex: 1, minHeight: 0 }}>
              <Box p={Spacing.XXL}>
                <Grid container spacing={Spacing.XL}>
                  <Grid size={4}>
                    <FormDropdown
                      name="movableAssetType"
                      options={movableAssets}
                      control={control}
                      disabled
                      labelEndAdornment={<StyledLabelAdornment />}
                      label={t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType.Title)}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={8}>
                    <BlFormInput
                      control={control}
                      disabled={readOnly}
                      name="name"
                      label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.Name)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                    />
                  </Grid>
                  <Grid size={12}>
                    <BlFormInput
                      control={control}
                      disabled
                      name="value"
                      label={t(Resources.AnalysisDetail.Assets.Value)}
                      labelEndAdornment={<StyledLabelAdornment />}
                      errors={errors}
                      unmask="typed"
                      placeholder="0"
                      mask={[
                        {
                          mask: Number,
                          thousandsSeparator: " ",
                          scale: 0,
                          min: 0,
                        },
                      ]}
                      inputEndAdornment={
                        <Typography variant="body2" color="secondary">
                          {t(Resources.Common.CZKSuffix)}
                        </Typography>
                      }
                    />
                  </Grid>

                  {isVehicle && (
                    <>
                      <Grid size={2}>
                        <BlFormInput
                          disabled={readOnly}
                          control={control}
                          name="vehicleLicensePlate"
                          label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehicleLicencePlate)}
                          errors={errors}
                        />
                      </Grid>
                      <Grid size={5}>
                        <BlFormInput
                          disabled={readOnly}
                          control={control}
                          name="vehicleIdentificationNumber"
                          label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VIN)}
                          errors={errors}
                        />
                      </Grid>
                      <Grid size={5}>
                        <BlFormInput
                          disabled={readOnly}
                          control={control}
                          name="vehiclePrice"
                          label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.VehiclePrice)}
                          errors={errors}
                          inputEndAdornment={
                            <Typography variant="body2" color="secondary">
                              {t(Resources.Common.CZKSuffix)}
                            </Typography>
                          }
                          mask={[{ mask: Number, thousandsSeparator: " ", scale: 0, min: 0 }]}
                        />
                      </Grid>
                    </>
                  )}

                  {isCompany && (
                    <>
                      <Grid size={12}>
                        <BlFormInput
                          disabled={readOnly}
                          control={control}
                          name="companyIdentificationNumber"
                          label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.CompanyIdentificationNumber)}
                          errors={errors}
                        />
                      </Grid>
                      <Grid size={12}>
                        <BlFormInput
                          disabled={readOnly}
                          control={control}
                          name="businessSector"
                          label={t(Resources.AnalysisDetail.AssetsAndContracts.Form.BusinessSector)}
                          errors={errors}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid size={12}>
                    <BlFormInput
                      disabled={readOnly}
                      control={control}
                      label={t(Resources.Common.Note)}
                      name="note"
                      errors={errors}
                      multiline
                      minRows={6}
                      maxRows={8}
                      characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DrawerContent>

            <Flex flexShrink={0} alignItems="center" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
              <Box>
                {!!movableAsset && (
                  <Button
                    variant="text"
                    color="error"
                    disabled={readOnly || isMutating}
                    sx={{
                      marginRight: Spacing.XL,
                    }}
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    {t(Resources.Common.Delete)}
                  </Button>
                )}
              </Box>

              <Flex flexDirection="row" justifyContent="flex-end">
                <Button
                  onClick={onClose}
                  color="secondary"
                  sx={{
                    marginRight: Spacing.XL,
                  }}
                >
                  {t(Resources.Common.Close)}
                </Button>
                <Button disabled={readOnly || isMutating} type="submit">
                  {movableAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
                </Button>
              </Flex>
            </Flex>
          </Box>
        </FormProvider>
      </Drawer>
      <DialogModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteAssetTitle)}
        subtitle={t(Resources.AnalysisDetail.AssetsAndContracts.Form.DeleteAssetText)}
        primaryAction={
          <Button disabled={readOnly || isMutating} onClick={onDelete}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </>
  );
};
